import { Divider, Stack, Box, useMediaQuery, IconButton, Tooltip } from '@mui/material';
import { Add, AddComment, Apps, Description, LocalOffer, Tag, Refresh } from '@mui/icons-material';
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import TextBox from '../../components/TextBox';
import CustomAutoComplete from '../../components/CustomAutoComplete';
import AutoCompleteBank from '../../components/AutoCompleteBank';
import ButtonSubmit from '../../components/ButtonSubmit';
import API from '../../services/api';
import { useNavigate } from 'react-router-dom';
import CustomSnackBar from '../../components/CustomSnackBar';
import { useDispatch, useSelector } from 'react-redux';
import { addTab, resetFormData, updateFormData } from '../../tabs/tabsSlice';
import MainText from '../../components/MainText';
import RecentProductsPanel from '../../components/RecentProductsPanel';
import { useAuth } from '../../auth/AuthProvider';

export default function CreateProduct() {
  const [openSnack, setOpenSnack] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('info');
  const navigate = useNavigate();
  const { company } = useAuth();

  const formData = useSelector((state) => state.tabs.forms.products);
  const dispatch = useDispatch();

  const [recentProducts, setRecentProducts] = useState([]);
  const isMobile = useMediaQuery('(max-width:768px)');
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [searchingHistoric, setSearchingHistoric] = useState(false);
  const [recentSearchTerm, setRecentSearchTerm] = useState('');
  const [historicProducts, setHistoricProducts] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [format, setFormat] = useState([]);
  const [cat, setCat] = useState([]);
  const [priceT, setPriceT] = useState([]);
  const recentAbortRef = useRef(null);
  const searchAbortRef = useRef(null);
  const catalogsAbortRef = useRef(null);
  const recentRequestIdRef = useRef(0);
  const searchRequestIdRef = useRef(0);
  const catalogsRequestIdRef = useRef(0);

  const normalizeId = (value, emptyValue = '') => {
    if (value === null || value === undefined || value === '') return emptyValue;
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  };
  const focusCodeInput = () => {
    setTimeout(() => {
      const el = document.getElementById('input-code');
      if (el) el.focus();
    }, 0);
  };

  const handleRefreshForm = () => {
    dispatch(resetFormData({ formName: 'products' }));
    setSelectedSupplier(null);
    setRecentSearchTerm('');
    setHistoricProducts([]);
    fetchRecentProducts(null);
    focusCodeInput();
  };

  const fetchRecentProducts = useCallback(async (supplierId = null) => {
    const normalizedSupplierId = normalizeId(supplierId, 0);
    const requestId = (recentRequestIdRef.current += 1);
    if (recentAbortRef.current) {
      recentAbortRef.current.abort();
    }
    const controller = new AbortController();
    recentAbortRef.current = controller;
    setLoadingRecent(true);
    try {
      let response;
      if (normalizedSupplierId) {
        response = await API.post(
          'product/getProductsBySupplier/',
          { supplier_id: normalizedSupplierId },
          { signal: controller.signal }
        );
      } else {
        response = await API.get('product/', {
          params: { active: true },
          signal: controller.signal,
        });
      }
      if (controller.signal.aborted || requestId !== recentRequestIdRef.current) return;
      const sorted = (response.data || [])
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
        .slice(0, 10);
      setRecentProducts(sorted);
    } catch (error) {
      if (controller.signal.aborted || error?.code === 'ERR_CANCELED') return;
      console.error(error);
    } finally {
      if (requestId === recentRequestIdRef.current) {
        setLoadingRecent(false);
      }
    }
  }, []);

  useEffect(() => {
    const term = String(recentSearchTerm || '').trim();
    if (!term) {
      setHistoricProducts([]);
      setSearchingHistoric(false);
      if (searchAbortRef.current) searchAbortRef.current.abort();
      return;
    }
    if (term.length < 3) {
      setHistoricProducts([]);
      setSearchingHistoric(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      const requestId = (searchRequestIdRef.current += 1);
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      const controller = new AbortController();
      searchAbortRef.current = controller;
      setSearchingHistoric(true);
      try {
        const params = {
          search: term,
          limit: 5,
          active: true,
        };
        const supplierFilter = normalizeId(formData?.supplier_id, 0);
        if (supplierFilter) params.supplier_id = supplierFilter;
        const response = await API.get('product/', {
          params,
          signal: controller.signal,
        });
        if (controller.signal.aborted || requestId !== searchRequestIdRef.current) return;
        const list = Array.isArray(response.data)
          ? response.data
          : (Array.isArray(response.data?.results) ? response.data.results : []);
        setHistoricProducts((list || []).slice(0, 5));
      } catch (error) {
        if (controller.signal.aborted || error?.code === 'ERR_CANCELED') return;
        setHistoricProducts([]);
        console.error(error);
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearchingHistoric(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [recentSearchTerm, formData?.supplier_id]);

  const fetchCatalogs = async () => {
    const requestId = (catalogsRequestIdRef.current += 1);
    if (catalogsAbortRef.current) {
      catalogsAbortRef.current.abort();
    }
    const controller = new AbortController();
    catalogsAbortRef.current = controller;
    try {
      const { data } = await API.get('catalogs/create_product/', {
        signal: controller.signal,
      });
      if (controller.signal.aborted || requestId !== catalogsRequestIdRef.current) return;
      const fmts = Array.isArray(data?.formats) ? data.formats : [];
      const cats = Array.isArray(data?.categories) ? data.categories : [];
      const pts = Array.isArray(data?.price_types) ? data.price_types : [];
      setFormat(fmts);
      setCat(cats);
      setPriceT(pts);
    } catch (e) {
      if (controller.signal.aborted || e?.code === 'ERR_CANCELED') return;
      // fallback vacÃ­o; el formulario seguirÃ¡ funcionando pero sin catÃ¡logos
      setFormat([]);
      setCat([]);
      setPriceT([]);
    }
  };

  useEffect(() => {
    dispatch(addTab({ label: 'Nuevo producto', path: '/orders/product/create-new' }));
    fetchRecentProducts(formData?.supplier_id || null);
    fetchCatalogs();
    return () => {
      if (recentAbortRef.current) recentAbortRef.current.abort();
      if (searchAbortRef.current) searchAbortRef.current.abort();
      if (catalogsAbortRef.current) catalogsAbortRef.current.abort();
    };
  }, [dispatch, fetchRecentProducts, formData?.supplier_id]);

  useEffect(() => {
    const companyId = normalizeId(company?.id, '');
    if (companyId && !formData?.company_id) {
      dispatch(updateFormData({ formName: 'products', field: 'company_id', value: companyId }));
    }
  }, [company?.id, formData?.company_id, dispatch]);

  const handleChange = (name, value) => {
    let nextValue = value;
    if (name === 'supplier_id') {
      const supplierRaw = typeof value === 'object' ? value?.id : value;
      const supplierId = normalizeId(supplierRaw, 0);
      fetchRecentProducts(supplierId || null);
      setSelectedSupplier(typeof value === 'object' ? value : null);
      nextValue = supplierId;
    }

    if (Object.prototype.hasOwnProperty.call(formData?.price || {}, name)) {
      const newPrice = { ...(formData?.price || {}), [name]: value };
      dispatch(updateFormData({ formName: 'products', field: 'price', value: newPrice }));
      return;
    }
    dispatch(updateFormData({ formName: 'products', field: name, value: nextValue }));
  };

  const handlePriceChange = (name, value) => {
    const filteredPrice = Object.fromEntries(
      Object.entries(formData?.price || {}).filter(([key]) => isNaN(key))
    );
    const newPrice = { ...filteredPrice, [name]: value };
    dispatch(updateFormData({ formName: 'products', field: 'price', value: newPrice }));
  };

  const handleSelectChange = (name, value) => {
    const raw = value ? (value.id ?? value.name ?? '') : '';
    const val = normalizeId(raw, '');
    dispatch(updateFormData({ formName: 'products', field: name, value: val }));
    // Guarda también _id para el backend
    if (name === 'prformat') {
      dispatch(updateFormData({ formName: 'products', field: 'prformat_id', value: val }));
    }
    if (name === 'category') {
      dispatch(updateFormData({ formName: 'products', field: 'category_id', value: val }));
    }
  };

  const handleSelectChangePrice = (name, value) => {
    const newPrice = {
      ...(formData?.price || {}),
      [name]: value?.name || '',
      [`${name}_id`]: value?.id || null,
    };
    dispatch(updateFormData({ formName: 'products', field: 'price', value: newPrice }));
  };

  const hasClonePayload = (product) => {
    if (!product || typeof product !== 'object') return false;
    const hasBase = Boolean(product.description) && (product.category || product.category_id || product.category?.id);
    const hasFormat = Boolean(product.prformat || product.prformat_id || product.prformat?.id);
    const hasPrice = Number.isFinite(Number(product?.price)) || (Array.isArray(product?.prices) && product.prices.length > 0);
    return hasBase && hasFormat && hasPrice;
  };

  const resolveCloneProduct = useCallback(async (product) => {
    if (!product) return null;
    if (hasClonePayload(product)) return product;
    if (!product?.id) return product;
    try {
      const { data } = await API.get(`product/${product.id}/`);
      return data || product;
    } catch (error) {
      console.error(error);
      return product;
    }
  }, []);

  const handleCloneProduct = async (product) => {
    const sourceProduct = await resolveCloneProduct(product);
    if (!sourceProduct) return;
    const categoryValue = normalizeId(sourceProduct.category?.id ?? sourceProduct.category_id ?? sourceProduct.category ?? '', '');
    const formatValue = normalizeId(sourceProduct.prformat?.id ?? sourceProduct.prformat_id ?? sourceProduct.prformat ?? '', '');
    const priceEntry = Array.isArray(sourceProduct.prices) ? (sourceProduct.prices[0] || null) : null;
    const priceType = priceEntry?.priceType || null;
    const companyValue = normalizeId(
      sourceProduct.company?.id ?? sourceProduct.company_id ?? formData?.company_id ?? company?.id ?? '',
      ''
    );

    if (sourceProduct.supplier || sourceProduct.supplier_id) {
      handleChange('supplier_id', sourceProduct.supplier || sourceProduct.supplier_id);
    }
    if (companyValue) {
      dispatch(updateFormData({ formName: 'products', field: 'company_id', value: companyValue }));
    }

    dispatch(updateFormData({ formName: 'products', field: 'code', value: '' }));
    dispatch(updateFormData({ formName: 'products', field: 'description', value: sourceProduct.description || '' }));
    dispatch(updateFormData({ formName: 'products', field: 'units', value: sourceProduct.units ?? 0 }));
    dispatch(updateFormData({ formName: 'products', field: 'category', value: categoryValue }));
    dispatch(updateFormData({ formName: 'products', field: 'category_id', value: categoryValue }));
    dispatch(updateFormData({ formName: 'products', field: 'prformat', value: formatValue }));
    dispatch(updateFormData({ formName: 'products', field: 'prformat_id', value: formatValue }));

    const nextPrice = {
      ...(formData?.price || {}),
      price: priceEntry?.price ?? sourceProduct.price ?? 0,
      priceType: priceType?.name || priceEntry?.priceType?.name || '',
      priceType_id: priceType?.id || priceEntry?.priceType_id || priceEntry?.priceType?.id || null,
    };
    dispatch(updateFormData({ formName: 'products', field: 'price', value: nextPrice }));
    focusCodeInput();
  };

  const validateForm = () => {
    const errors = [];
    if (!String(formData?.code || '').trim()) errors.push('Código');
    if (!String(formData?.description || '').trim()) errors.push('Descripción');
    const units = Number(formData?.units);
    if (!Number.isFinite(units) || units <= 0) errors.push('Unidades');
    if (!formData?.supplier_id) errors.push('Proveedor');
    const companyId = formData?.company_id || company?.id;
    if (!companyId) errors.push('Compania');
    if (!formData?.category) errors.push('Categoría');
    if (!formData?.prformat) errors.push('Formato');
    const priceVal = Number(formData?.price?.price);
    if (!Number.isFinite(priceVal) || priceVal <= 0) errors.push('Precio (>0)');
    if (errors.length > 0) {
      setSnackMessage(`Completa los campos requeridos: ${errors.join(', ')}`);
      setSnackSeverity('error');
      setOpenSnack(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e, continueCreating = false) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        company_id: formData?.company_id || company?.id || null,
      };
      await API.post('product/createProductPrice/', payload);
      fetchRecentProducts(formData?.supplier_id || null);
      setOpenSnack(true);
      setSnackMessage('¡Creado con éxito!');
      setSnackSeverity('success');
      setTimeout(() => {
        setLoading(false);
        if (continueCreating) {
          dispatch(resetFormData({ formName: 'products' }));
        } else {
          navigate('/orders/product/all-products');
          dispatch(resetFormData({ formName: 'products' }));
        }
      }, 1500);
    } catch (error) {
      setOpenSnack(true);
      setLoading(false);
      if (error.response?.data?.message) {
        setSnackMessage(error.response.data.message);
        setSnackSeverity('error');
      } else if (error.message) {
        setSnackMessage(error.message);
        setSnackSeverity('error');
      } else {
        setSnackMessage('Ocurrió un error inesperado. Intente de nuevo.');
        setSnackSeverity('error');
      }
    }
  };

  const supplierIdValue = normalizeId(formData?.supplier_id, 0);
  const currentFormatValue = formData?.prformat_id ?? formData?.prformat;
  const currentCategoryValue = formData?.category_id ?? formData?.category;
  const selectedFormat = useMemo(
    () =>
      (format || []).find(
        (f) => String(f.id) === String(currentFormatValue) || f.name === currentFormatValue
      ) || null,
    [currentFormatValue, format]
  );
  const selectedCategory = useMemo(
    () =>
      (cat || []).find(
        (c) => String(c.id) === String(currentCategoryValue) || c.name === currentCategoryValue
      ) || null,
    [currentCategoryValue, cat]
  );
  const selectedPriceType = useMemo(() => (priceT || []).find(p => String(p.id) === String(formData?.price?.priceType_id || formData?.price?.priceType) || p.name === formData?.price?.priceType) || null, [formData?.price?.priceType, formData?.price?.priceType_id, priceT]);
  const showHistoricSearchResults = String(recentSearchTerm || '').trim().length >= 3;
  const panelProducts = useMemo(
    () => (showHistoricSearchResults ? (historicProducts || []).slice(0, 5) : (recentProducts || [])),
    [showHistoricSearchResults, historicProducts, recentProducts]
  );

  return (
    <Stack
      spacing={15}
      direction={{ xs: 'column', md: 'row' }}
      sx={{ flexWrap: 'wrap', alignItems: 'flex-start' }}
    >
      <Box
        component="form"
        onSubmit={(e) => handleSubmit(e, false)}
        flex={1}
        sx={{ minWidth: 0, maxWidth: '100%' }}
      >
        <MainText title={'Nuevo producto'} subtitle={'Ingreso de información'} icon={AddComment} />

        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
          <TextBox
            label='Codigo'
            icon={Tag}
            required
            maxLength={10}
            name={'code'}
            errorMessage='Campo requerido'
            onChange={handleChange}
            type='text'
            width={'100px'}
            newValue={formData?.code || ''}
          />
          <TextBox
            label='Unidades'
            icon={Apps}
            type='number'
            required
            width={'100px'}
            maxLength={10}
            name={'units'}
            errorMessage='Campo requerido'
            onChange={handleChange}
            newValue={formData?.units ?? 0}
          />
        </Stack>

        <TextBox
          label='Descripción'
          icon={Description}
          width={'590px'}
          required
          maxLength={40}
          name={'description'}
          errorMessage='Campo requerido'
          onChange={handleChange}
          type='text'
          newValue={formData?.description || ''}
        />

        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
          <AutoCompleteBank
            label='Formato'
            options={format}
            getOptionLabel={(f) => f.name}
            onChange={handleSelectChange}
            name={'prformat'}
            value={selectedFormat}
          />
          <AutoCompleteBank
            label='Categoria'
            options={cat}
            getOptionLabel={(c) => c.name}
            onChange={handleSelectChange}
            name={'category'}
            value={selectedCategory}
          />
        </Stack>

        <CustomAutoComplete
          name={'supplier_id'}
          onChange={handleChange}
          supplier={supplierIdValue}
        />

        <Divider sx={{ width: '15%' }} variant='fullWidth'>
          Datos del precio
        </Divider>

        <TextBox
          label='Precio'
          type='number'
          icon={LocalOffer}
          required
          maxLength={10}
          name={'price'}
          errorMessage='Campo requerido'
          onChange={handlePriceChange}
          newValue={formData?.price?.price ?? 0}
        />

        <AutoCompleteBank
          label='Tipo de precio'
          options={priceT}
          getOptionLabel={(p) => p.name}
          onChange={(event, value) => handleSelectChangePrice('priceType', value)}
          name={'priceType'}
          value={selectedPriceType}
        />

        <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
          <ButtonSubmit name='Crear' icon={Add} loading={loading} />
          <ButtonSubmit
            name='Crear y continuar'
            icon={Add}
            loading={loading}
            color='primary'
            type='button'
            onClick={(e) => handleSubmit(e, true)}
          />
          <Tooltip title="Limpiar campos" arrow>
            <IconButton
              color="primary"
              onClick={handleRefreshForm}
              sx={{ alignSelf: 'center' }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>

        {openSnack && (
          <CustomSnackBar
            open={openSnack}
            setOpen={setOpenSnack}
            message={snackMessage}
            severity={snackSeverity}
          />
        )}
      </Box>
      {!isMobile && (
        <RecentProductsPanel
          items={panelProducts}
          loading={loadingRecent && !showHistoricSearchResults}
          searching={searchingHistoric}
          searchTerm={recentSearchTerm}
          onSearchTermChange={setRecentSearchTerm}
          supplierName={selectedSupplier?.companyName}
          pageSize={5}
          onClone={handleCloneProduct}
          onRefresh={() => {
            setRecentSearchTerm('');
            setHistoricProducts([]);
            fetchRecentProducts(supplierIdValue || null);
            fetchCatalogs();
          }}
          sx={{
            ml: { md: 4 },
            width: '100%',
            maxWidth: { md: 440, lg: 480 },
            flex: '1 1 360px'
          }}
        />
      )}

    </Stack>
  );
}

